/**
 * Keeping every strategy resolved and priced — ROADMAP 5.5.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ON A TIMER, NOT ON EVERY TICK
 *
 * §4.7's rule is that ticks never touch React state: a chain at 12 Hz across
 * hundreds of instruments would re-render the tree faster than it can paint.
 * The same applies here and harder — one tick anywhere in the chain changes
 * the premium of every strategy that happens to use that strike, so a
 * tick-driven preview would recompute fourteen baskets on every print.
 *
 * So the whole set is recomputed on a fixed, slow cadence by reading the tick
 * store directly. A premium that is a second old is not a problem: it is an
 * estimate shown before a decision, and the ORDER is priced separately at
 * dispatch off the engine's own book ([B19]).
 *
 * WHY THE PREVIEW CAN DISAGREE WITH A FULL CHAIN
 *
 * It resolves against the strikes the browser has LOADED, which is a window
 * around ATM, not the whole ladder. A `CLOSEST_PREMIUM` target far outside
 * that window matches the best strike inside it instead — and the answer may
 * change as the window widens.
 *
 * That is why placing sends the RESOLVED TOKENS rather than the criteria: the
 * trader approved four specific contracts, and re-resolving server-side
 * against a fuller chain could trade strikes they never saw. This is the
 * opposite choice from the exit path, deliberately — there, "close what I
 * hold" is a question about current state; here, "buy these four" is a choice
 * already made.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  netPremiumOf,
  resolveLegs,
  type ChainQuote,
  type ExpiryKind,
  type ResolvedLeg,
  type StrategyTemplateLeg,
  type StrikeCriteria,
  type UnresolvedLeg,
} from '@apna/shared'
import { tickStore } from '@/feed/tickStore'
import type { StrategyDto } from '@/types/strategy'

/** One row of the chain as the Terminal already holds it. */
export interface PreviewChainRow {
  strike: number
  ceToken: number | null
  peToken: number | null
}

export interface StrategyPreview {
  strategyId: string
  /** Null while it cannot be resolved; `why` then says what is missing. */
  legs: readonly ResolvedLeg[] | null
  atmStrike: number | null
  /** Signed rupees, positive for a credit. Null when the book cannot price it. */
  netPremium: number | null
  why: string | null
  /**
   * The per-leg reasons behind `why`, when resolution is what failed.
   *
   * Carried because the headline is a COUNT — "6 of 6 leg(s) could not be
   * resolved" — and a count is not actionable. The causes are: an unlisted
   * strike is a different problem from an expiry that is not loaded, and
   * the trader can only fix the one they are told about.
   */
  unresolved: readonly UnresolvedLeg[]
}

export interface PreviewInputs {
  strategies: readonly StrategyDto[]
  rows: readonly PreviewChainRow[]
  spot: number
  step: number
  lotSize: number
  /**
   * THE SIZE THE HEADER IS SET TO, in lots.
   *
   * Falls back to each strategy's own `defaultLots` when absent. It used to
   * ALWAYS use the default, so a trader who set 10 lots and fired a basket
   * got the saved size — the preview, the premium and the order all agreed
   * with each other and all disagreed with the control they had just used.
   */
  lots?: number | undefined
  /** Milliseconds between recomputes. One second is far faster than a human. */
  cadenceMs?: number
  /** False while the picker is closed, so nothing is computed for no reader. */
  enabled?: boolean
  /**
   * Which expiry kinds `rows` answers to — `kindsOfExpiry(expiry, expiries)`.
   *
   * Without it every leg carrying a stored `expiryKind` is read as a
   * calendar leg wanting a chain this hook does not hold, and refused —
   * including the overwhelmingly common case of a leg that names the very
   * expiry on screen.
   */
  chainKinds?: readonly ExpiryKind[]
  /**
   * Ladders for the OTHER expiries legs name — the calendar case, 9.43.
   *
   * Keyed by KIND rather than by date because that is what a leg stores and
   * what the resolver compares against. Absent means no leg may name
   * another expiry, which is a refusal and never a fallback to `rows`:
   * resolving a far-month leg against the near week builds a position that
   * is not a calendar spread while being priced as one.
   */
  rowsByKind?: ReadonlyMap<ExpiryKind, readonly PreviewChainRow[]>
  /**
   * Solved delta per contract — ROADMAP 5.23.
   *
   * Required by `CLOSEST_DELTA` and `DELTA_RANGE` legs and by nothing else,
   * so it is optional: a caller with no greeks surface previews every other
   * strategy normally and sees a delta leg REFUSE by name. Build it with
   * `deltaReaderOf` over the pass the chain column already runs rather than
   * solving a second time — see that function for why.
   */
  deltaOf?: (token: number) => number | null
}

/**
 * A leg's criteria, or null when the stored rule did not parse.
 *
 * An unreadable rule makes the WHOLE strategy unpreviewable rather than being
 * skipped — three legs of a four-leg condor priced as if it were the strategy
 * would be a number for a position nobody would hold.
 */
function legsOf(s: StrategyDto): StrategyTemplateLeg[] | null {
  const out: StrategyTemplateLeg[] = []
  for (const leg of s.legs) {
    if (!leg.strikeCriteria) return null
    out.push({
      side: leg.side,
      optionType: leg.optionType,
      strikeCriteria: leg.strikeCriteria as StrikeCriteria,
      ratio: leg.ratio,
      /*
       * CARRIED, not dropped. Omitting it made a leg on another week
       * resolve against THIS one — two legs of a calendar landing on the
       * same contract, to be merged or refused by `collapse` with no hint
       * that the structure had been changed. Carried, the resolver refuses
       * it by name until a second chain is supplied.
       */
      expiryKind: leg.expiryKind,
    })
  }
  return out
}

/**
 * One ladder's rows as the resolver's quotes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY LISTED CONTRACT, PRICED OR NOT — ROADMAP 9.45
 *
 * This used to skip a strike with no slot in the tick store, which quietly
 * undid 9.40. That change widened `previewRows` from the rendered window to
 * the whole catalogue so a leg on an unscrolled strike could resolve; this
 * function then narrowed it straight back to the SUBSCRIBED set, because
 * only the window is ever subscribed.
 *
 * The symptom, reported on 2026-11-03: "24350 CE is not listed for this
 * expiry" on a contract that is listed, with "Show 10 higher strikes" sitting
 * under the ladder — and pressing it fixed the error, which is the tell. A
 * trader should never have to scroll a table to make a basket resolvable.
 *
 * WHICH CRITERIA THIS IS SAFE FOR, AND WHY IT IS ALL OF THEM. An `ATM_OFFSET`
 * leg names a strike and needs no price to be SELECTED, so it resolves from
 * the catalogue alone. Premium and delta legs DO need one, and they are
 * already protected one layer down: `candidates` takes only `ltp > 0`, so an
 * unpriced row cannot win a `CLOSEST_PREMIUM` or a `DELTA_RANGE` — it is
 * invisible to them exactly as it was before.
 *
 * A resolved leg with no price is then subscribed by whoever displays it (the
 * picker asks for its own legs while open) and the price arrives a beat
 * later. "Resolved but not yet priceable" is the honest half-answer; "not
 * listed" was a false one.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function quotesOf(ladder: readonly PreviewChainRow[]): ChainQuote[] {
  const out: ChainQuote[] = []
  for (const row of ladder) {
    for (const [token, optionType] of [
      [row.ceToken, 'CE'],
      [row.peToken, 'PE'],
    ] as const) {
      if (!token) continue
      const slot = tickStore.slotFor(token)
      if (slot < 0) {
        // Listed, not subscribed. Zero is not a price and nothing treats it
        // as one — `candidates` drops it, and `netPremiumOf` says the basket
        // is not priceable rather than pricing it at nothing.
        out.push({ token, strike: row.strike, optionType, ltp: 0 })
        continue
      }
      out.push({
        token,
        strike: row.strike,
        optionType,
        ltp: tickStore.field(slot, 'ltp'),
        bid: tickStore.field(slot, 'bid'),
        ask: tickStore.field(slot, 'ask'),
      })
    }
  }
  return out
}

export function useStrategyPreviews({
  strategies,
  rows,
  spot,
  step,
  lotSize,
  lots,
  deltaOf,
  chainKinds,
  rowsByKind,
  cadenceMs = 1_000,
  enabled = true,
}: PreviewInputs): Map<string, StrategyPreview> {
  const [previews, setPreviews] = useState<Map<string, StrategyPreview>>(new Map())

  /*
   * The inputs are held in a ref as well as closed over, so the interval can
   * be created ONCE. Recreating it whenever a price-adjacent prop changes
   * would reset the cadence continuously and, at worst, never fire.
   */
  const latest = useRef({ strategies, rows, spot, step, lotSize, lots, deltaOf, chainKinds, rowsByKind })
  latest.current = { strategies, rows, spot, step, lotSize, lots, deltaOf, chainKinds, rowsByKind }

  const legsById = useMemo(() => {
    const m = new Map<string, StrategyTemplateLeg[] | null>()
    for (const s of strategies) m.set(s.id, legsOf(s))
    return m
  }, [strategies])
  const legsRef = useRef(legsById)
  legsRef.current = legsById

  /*
   * Computing is a `useCallback` over refs rather than a closure inside the
   * interval effect, so there are TWO triggers with one implementation:
   * the cadence, and any change to the strategy set. Without the second, a
   * list that loads after mount shows no premiums until the next tick of the
   * timer — which a test caught, and which a user would read as "this
   * strategy cannot be priced".
   *
   * Prices are deliberately NOT a trigger. They change constantly; that is
   * what the cadence is for.
   */
  const compute = useCallback((): void => {
    const {
      strategies: list,
      rows: ladder,
      spot: px,
      step: st,
      lotSize: ls,
      lots: sized,
      deltaOf: dOf,
      chainKinds: kinds,
      rowsByKind: byKind,
    } = latest.current

    // The chain is read ONCE per pass and shared by every strategy, rather
    // than each one walking the tick store for itself.
    const chain = quotesOf(ladder)
    /*
     * The other expiries' ladders, for calendar legs. Built in the same
     * pass and by the same function, so a far-week leg is priced exactly
     * the way a near one is rather than through a second code path.
     */
    const chainByExpiry = byKind === undefined
      ? undefined
      : new Map([...byKind].map(([kind, r]) => [kind, quotesOf(r)] as const))

    const next = new Map<string, StrategyPreview>()
    for (const s of list) {
      const legs = legsRef.current.get(s.id)
      if (!legs) {
        next.set(s.id, {
          strategyId: s.id,
          legs: null,
          atmStrike: null,
          netPremium: null,
          why: 'A leg has a strike rule this version cannot read.',
          unresolved: [],
        })
        continue
      }

      const resolved = resolveLegs(legs, {
        spot: px,
        step: st,
        lots: sized ?? s.defaultLots,
        chain,
        /*
         * WHICH expiry `chain` is, so a leg naming it resolves against it.
         * `chainByExpiry` stays deliberately absent: this hook holds ONE
         * expiry, so a leg naming a DIFFERENT one is refused by name rather
         * than resolved against the wrong week.
         */
        ...(kinds && kinds.length > 0 ? { chainKinds: kinds } : {}),
        ...(chainByExpiry && chainByExpiry.size > 0 ? { chainByExpiry } : {}),
        ...(dOf ? { deltaOf: dOf } : {}),
      })
      if (!resolved.ok) {
        next.set(s.id, {
          strategyId: s.id,
          legs: null,
          atmStrike: null,
          netPremium: null,
          why: resolved.why,
          unresolved: resolved.unresolved,
        })
        continue
      }

      const priced = netPremiumOf(resolved.legs, ls)
      next.set(s.id, {
        strategyId: s.id,
        legs: resolved.legs,
        atmStrike: resolved.atmStrike,
        /*
         * Resolving and PRICING fail independently: a basket can be fully
         * resolved and still unpriceable when one side of a book is empty,
         * and the legs are worth showing either way.
         */
        netPremium: priced.ok ? priced.netPremium : null,
        why: priced.ok ? null : priced.why,
        unresolved: [],
      })
    }
    setPreviews(next)
  }, [])

  /** Recompute immediately when the set of strategies changes, or on open. */
  useEffect(() => {
    if (!enabled) {
      setPreviews(new Map())
      return
    }
    compute()
  }, [enabled, compute, legsById])

  /** And on a slow cadence while open, for prices. */
  useEffect(() => {
    if (!enabled) return
    const timer = window.setInterval(compute, cadenceMs)
    return () => window.clearInterval(timer)
  }, [enabled, cadenceMs, compute])

  return previews
}
